// frontend/src/components/paywall/UpgradeSection.tsx

import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { auth } from '../../config/firebase';
import { apiService } from '../../services/api';
import { RedemptionCodeInput } from './RedemptionCodeInput';

/**
 * Initialize Stripe
 */
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripePublishableKey) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
}
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

/**
 * Feature list for Pro plan
 */
const FEATURES = [
  'Solution verification',
  'AI tutor chat',
  'Algorithm animations',
  'Document extraction',
  'Collection generation',
  'Monthly auto-renewal',
];

/**
 * Check icon SVG
 */
const CheckIcon: React.FC = () => (
  <svg className="feature-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
  </svg>
);

/**
 * Upgrade Section Props
 */
export interface UpgradeSectionProps {
  /**
   * Callback when upgrade button is clicked
   * Default: Navigate to Stripe payment page (Phase 3)
   */
  onUpgradeClick?: () => void;
  /**
   * Callback when a redemption code is successfully applied
   * Used to refresh usage state and close the modal
   */
  onRedemptionSuccess?: () => void;
}

/**
 * Upgrade Section Component
 * Displays the Pro upgrade CTA with features, pricing, and promotional messaging
 */
export const UpgradeSection: React.FC<UpgradeSectionProps> = ({ onUpgradeClick, onRedemptionSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle successful redemption code
   */
  const handleRedemptionSuccess = () => {
    // Call the parent callback to refresh usage and close modal
    onRedemptionSuccess?.();
  };

  /**
   * Handle upgrade button click
   * Redirects to Stripe Checkout
   */
  const handleUpgrade = async () => {
    // If custom handler provided, use it
    if (onUpgradeClick) {
      onUpgradeClick();
      return;
    }

    // Check if user is authenticated
    if (!auth.currentUser) {
      setError('Please sign in to upgrade to Pro');
      return;
    }

    // Check if Stripe is configured
    if (!stripePromise) {
      setError('Payment system is not configured. Please contact support.');
      console.error('Stripe publishable key is missing');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get current page URL for success/cancel redirects
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/payment/success`;
      const cancelUrl = `${currentUrl}/payment/canceled`;

      // Call createCheckoutSession Cloud Function
      // Backend returns: { success: true, data: { sessionId, url } }
      const response = await apiService.request<{ sessionId: string; url: string }>(
        '/auth/checkout-session',
        {
          method: 'POST',
          body: JSON.stringify({
            successUrl,
            cancelUrl,
          }),
        }
      );

      if (!response.data?.url) {
        throw new Error('Failed to create checkout session');
      }

      // Save current path for return after successful payment
      const currentPath = window.location.pathname;
      localStorage.setItem('aiprep_return_after_upgrade', currentPath);
      console.log('[Upgrade] Saved return path:', currentPath);

      // Redirect to Stripe Checkout URL
      // Using direct URL redirect instead of deprecated redirectToCheckout method
      window.location.href = response.data.url;
    } catch (err: any) {
      console.error('Failed to start checkout:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="upgrade-section">
      <span className="promo-badge">LIMITED TIME: 70% OFF</span>

      <h2 className="upgrade-title">Upgrade to Pro</h2>
      <p className="upgrade-subtitle">Continue your learning journey with powerful AI helps</p>

      <div className="credits-highlight">
        <div className="credits-number">10,000</div>
        <div className="credits-label">AI HELPS EVERY MONTH</div>
      </div>

      <div className="features-grid">
        {FEATURES.map((feature, index) => (
          <div key={index} className="feature-item">
            <CheckIcon />
            <span>{feature}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="error-message" role="alert" style={{
          color: '#ef4444',
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <button
        className="upgrade-button"
        onClick={handleUpgrade}
        disabled={isProcessing}
        aria-label="Upgrade to Pro plan"
        style={{ opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
      >
        {isProcessing ? 'Processing...' : 'Upgrade to Pro Now'}
      </button>

      <div className="pricing-display">
        <span className="original-price">$99.00</span>
        <span className="current-price">$29.99</span>
        <span className="price-period">/month</span>
        <span className="savings-badge">SAVE 70%</span>
      </div>

      {/* Redemption code input section */}
      <RedemptionCodeInput onSuccess={handleRedemptionSuccess} />
    </div>
  );
};
